/** @type {{ focusin?: (event: FocusEvent) => void; focusout?: () => void; keydown?: (event: KeyboardEvent) => void; addedTabindexContainer?: HTMLElement | null }} */
const trapFocusHandlers = {};

/**
 * Get all focusable (tabbable) elements within a container.
 * Custom implementation without tabbable package.
 *
 * @param {HTMLElement} container - The container to get focusable elements from.
 * @returns {HTMLElement[]} An array of focusable elements.
 */
function getFocusableElements(container) {
  const selectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex^="-"])',
    'summary',
    '[draggable="true"]',
    'area[href]',
    'object',
    'iframe',
  ].join(', ');

  return /** @type {HTMLElement[]} */ (
    Array.from(container.querySelectorAll(selectors)).filter((el) => {
      const htmlEl = /** @type {HTMLElement} */ (el);

      // Skip elements that are not rendered on the page (e.g. inside a hidden parent),
      // unless it's a <summary> tag which can still be visible despite having no offsetParent
      if (htmlEl.offsetParent === null && htmlEl.tagName !== 'SUMMARY') return false;

      // Skip elements that are explicitly hidden via CSS
      const style = getComputedStyle(htmlEl);
      if (style.visibility === 'hidden' || style.display === 'none') return false;

      return true;
    })
  );
}

/** @param {KeyboardEvent} e */
const handleFirstTab = (e) => {
  if (e.key === 'Tab') {
    document.body.classList.add('user-is-tabbing');
    window.removeEventListener('keydown', handleFirstTab);
    window.addEventListener('mousedown', handleMouseDownOnce);
  }
};

const handleMouseDownOnce = () => {
  document.body.classList.remove('user-is-tabbing');
  window.removeEventListener('mousedown', handleMouseDownOnce);
  window.addEventListener('keydown', handleFirstTab);
};

window.addEventListener('keydown', handleFirstTab);

/**
 * Remove focus trap and optionally refocus an element.
 *
 * @param {HTMLElement | null} [elementToFocus=null] - Element to focus when trap is removed.
 */
function removeTrapFocus(elementToFocus = null) {
  if (trapFocusHandlers.focusin) document.removeEventListener('focusin', trapFocusHandlers.focusin);
  if (trapFocusHandlers.focusout)
    document.removeEventListener('focusout', trapFocusHandlers.focusout);
  if (trapFocusHandlers.keydown) document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (trapFocusHandlers.addedTabindexContainer) {
    trapFocusHandlers.addedTabindexContainer.removeAttribute('tabindex');
    trapFocusHandlers.addedTabindexContainer = null;
  }

  if (elementToFocus) {
    elementToFocus.focus();
  }
}

/**
 * Trap focus within the given container.
 *
 * @param {HTMLElement} container - The container to trap focus within.
 * @param {HTMLElement} [elementToFocus=container] - Element to focus when trap is activated.
 */
function trapFocus(container, elementToFocus = container) {
  removeTrapFocus();
  const elements = getFocusableElements(container);

  // Validate that the requested focus target actually lives inside the container
  const requestedTarget =
    elementToFocus instanceof HTMLElement &&
      (elementToFocus === container || container.contains(elementToFocus))
      ? elementToFocus
      : null;

  // Decide where to send focus: the requested element → first focusable element → the container itself as a fallback
  const focusTarget = requestedTarget || elements[0] || container;

  const keydownHandler = (/** @type {KeyboardEvent} */ event) => {
    const target = event.target;

    // Ignore keystrokes that happen outside the trapped container
    if (!(target instanceof Node) || (!container.contains(target) && target !== container)) return;

    // Re-query focusable elements each time in case the DOM has changed (e.g. dynamic content)
    const elements = getFocusableElements(container);
    const first = elements[0];
    const last = elements[elements.length - 1];

    if (event.code?.toUpperCase() !== 'TAB') return;

    // If focus is on the LAST element and the user presses Tab (forward),
    // loop back around to the FIRST element instead of leaving the container
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first?.focus();
    }

    // If focus is on the FIRST element (or the container itself) and the user presses Shift+Tab (backward),
    // loop back around to the LAST element instead of leaving the container
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last?.focus();
    }
  };
  trapFocusHandlers.keydown = keydownHandler;
  document.addEventListener('keydown', keydownHandler);

  // If the container itself is the focus target but isn't natively focusable,
  // temporarily add tabindex="-1" so we can programmatically focus it
  if (focusTarget === container && !container.hasAttribute('tabindex')) {
    container.setAttribute('tabindex', '-1');

    // Track that we added this so we can remove it when the trap is released
    trapFocusHandlers.addedTabindexContainer = container;
  }
  focusTarget.focus();

  // If the focused element is a text input, select all its existing content
  // so the user can start typing immediately without having to manually clear it
  if (focusTarget instanceof HTMLInputElement) {
    const input = focusTarget;
    if (['search', 'text', 'email', 'url'].includes(input.type) && input.value) {
      input.setSelectionRange(0, input.value.length);
    }
  }
}

/**
 * Cycle focus to the next or previous link
 *
 * @param {HTMLElement[]} items
 * @param {number} increment
 */
function cycleFocus(items, increment) {
  const currentIndex = items.findIndex((item) => item.matches(':focus'));
  let targetIndex = currentIndex + increment;

  // If we've gone past the end, wrap around to the first item
  if (targetIndex >= items.length) {
    targetIndex = 0;

    // If we've gone before the start, wrap around to the last item
  } else if (targetIndex < 0) {
    targetIndex = items.length - 1;
  }

  const targetItem = items[targetIndex];

  if (!targetItem) return;

  targetItem.focus();
}

export { trapFocus, removeTrapFocus, getFocusableElements, cycleFocus };
